import { Link } from "react-router-dom";
import { Hexagon, ArrowRight, Clock, CalendarDays, BookOpen } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import AffiliateCTA from "@/components/AffiliateCTA";
import { Reveal } from "@/components/Reveal";
import { POSTS } from "@/data/posts";

const Blog = () => (
  <div className="min-h-screen bg-[#FAF8F5] honey-grain font-body text-zinc-900">
    <SeoHead
      title="Blog — Passive Income Guides & Tips | HoneyGain Deals"
      description="Practical guides on earning passive income daily — bandwidth sharing, savings, dividends, and apps that pay you every day."
      path="/blog"
    />
    <header className="border-b border-amber-100 bg-white/80 backdrop-blur-md sticky top-0 z-40">
      <div className="mx-auto max-w-5xl px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-lg text-zinc-900" data-testid="blog-logo">
          <span className="grid place-items-center w-8 h-8 rounded-xl bg-amber-400 text-zinc-900">
            <Hexagon className="w-5 h-5" strokeWidth={2.5} />
          </span>
          HoneyGain<span className="text-amber-600">Deals</span>
          <span className="hidden sm:inline-flex ml-2 rounded-full bg-amber-100 border border-amber-300 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">Blog</span>
        </Link>
        <AffiliateCTA label="Get $3 Bonus" testid="blog-header-cta" dark />
      </div>
    </header>

    <main className="mx-auto max-w-5xl px-5 sm:px-8 py-16 sm:py-20">
      <Reveal>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-700 inline-flex items-center gap-2">
          <BookOpen className="w-4 h-4" /> The passive income blog
        </p>
        <h1 className="mt-3 font-display text-3xl sm:text-5xl font-extrabold tracking-tight text-zinc-900 max-w-2xl leading-tight">
          Guides that help you earn every single day
        </h1>
        <p className="mt-4 text-base sm:text-lg text-zinc-600 max-w-2xl">
          No fluff, no get-rich-quick schemes — just realistic methods, real numbers, and honest
          comparisons.
        </p>
      </Reveal>

      <div className="mt-12 grid gap-6">
        {POSTS.map((post, i) => (
          <Reveal key={post.slug} delay={i * 0.08}>
            <Link
              to={`/blog/${post.slug}`}
              data-testid={`blog-card-${post.slug}`}
              className="group block rounded-3xl border border-zinc-100 bg-white p-8 sm:p-10 shadow-sm hover:shadow-xl hover:shadow-amber-900/5 hover:-translate-y-1 transition-all"
            >
            <img
              src={post.image}
              alt={post.title}
              loading="lazy"
              className="mb-7 w-full aspect-[16/9] object-cover rounded-2xl border border-zinc-100"
            />
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-semibold text-zinc-500">
                <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {post.date}</span>
                <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {post.readTime}</span>
                <span className="rounded-full bg-amber-100 border border-amber-200 px-2.5 py-0.5 text-amber-800">Featured guide</span>
              </div>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-zinc-900 group-hover:text-amber-700 transition-colors leading-snug">
                {post.title}
              </h2>
              <p className="mt-3 text-base text-zinc-600 leading-relaxed max-w-3xl">{post.excerpt}</p>
              <span className="mt-5 inline-flex items-center gap-2 font-display font-bold text-sm text-amber-700">
                Read the guide
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15} className="mt-14">
        <div className="rounded-3xl bg-zinc-900 px-8 py-10 sm:px-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display text-2xl font-extrabold text-white">Skip the reading — start earning</h2>
            <p className="mt-2 text-sm text-zinc-400">Free to join, two minutes to set up, $3 bonus through our link.</p>
          </div>
          <AffiliateCTA label="Join Honeygain Free" testid="blog-index-bottom-cta" />
        </div>
      </Reveal>
    </main>
  </div>
);

export default Blog;
